// API layer for LeadMap3D

import type { Business, Coordinates, LeadFilter, Client, WebsiteBuild, WebsiteTemplate, User, PricingPlan } from '../types';
import { generateId } from '../lib/utils';

// Mock data generators for development
const CATEGORIES = [
  'restaurant', 'cafe', 'bar', 'retail', 'health', 'beauty', 'fitness',
  'automotive', 'home', 'professional', 'education', 'entertainment',
  'travel', 'finance', 'legal', 'real-estate', 'technology', 'construction'
];

const SUBCATEGORIES: Record<string, string[]> = {
  'restaurant': ['italian', 'mexican', 'chinese', 'american', 'japanese', 'indian', 'thai', 'mediterranean'],
  'cafe': ['coffee', 'bakery', 'tea', 'dessert'],
  'bar': ['pub', 'lounge', 'brewery', 'wine-bar', 'cocktail'],
  'retail': ['clothing', 'electronics', 'home-goods', 'books', 'sports', 'jewelry'],
  'health': ['dentist', 'doctor', 'chiropractor', 'pharmacy', 'optometrist', 'therapist'],
  'beauty': ['salon', 'spa', 'nails', 'barber', 'skincare', 'massage'],
  'fitness': ['gym', 'yoga', 'pilates', 'crossfit', 'martial-arts', 'pool'],
  'automotive': ['repair', 'dealership', 'car-wash', 'tires', 'parts'],
  'home': ['plumber', 'electrician', 'hvac', 'roofing', 'landscaping', 'cleaning'],
  'professional': ['lawyer', 'accountant', 'consultant', 'insurance', 'real-estate-agent'],
  'education': ['school', 'tutoring', 'language', 'music', 'art', 'dance'],
  'entertainment': ['theater', 'cinema', 'bowling', 'arcade', 'escape-room', 'museum'],
  'travel': ['hotel', 'airbnb', 'travel-agent', 'tour-operator', 'car-rental'],
  'finance': ['bank', 'credit-union', 'financial-advisor', 'tax-prep', 'investment'],
  'legal': ['lawyer', 'notary', 'mediation', 'paralegal'],
  'real-estate': ['agency', 'property-management', 'appraisal', 'inspection'],
  'technology': ['software', 'it-support', 'web-design', 'cybersecurity', 'data'],
  'construction': ['general-contractor', 'remodeling', 'concrete', 'electrical', 'plumbing'],
};

const STREET_NAMES = [
  'Main St', 'Oak Ave', 'Park Blvd', 'Elm St', 'Maple Dr', 'Cedar Ln', 'Pine Rd',
  'Washington Ave', 'Lincoln St', 'Jefferson Blvd', 'Madison Ave', 'Monroe St',
  'First St', 'Second St', 'Third Ave', 'Fourth St', 'Fifth Ave', 'Broadway',
  'Market St', 'Church St', 'School St', 'High St', 'Lowell St', 'Spring St',
];

const CITIES = [
  { name: 'New York', state: 'NY', lat: 40.7128, lng: -74.0060 },
  { name: 'Los Angeles', state: 'CA', lat: 34.0522, lng: -118.2437 },
  { name: 'Chicago', state: 'IL', lat: 41.8781, lng: -87.6298 },
  { name: 'Houston', state: 'TX', lat: 29.7604, lng: -95.3698 },
  { name: 'Phoenix', state: 'AZ', lat: 33.4484, lng: -112.0740 },
  { name: 'Philadelphia', state: 'PA', lat: 39.9526, lng: -75.1652 },
  { name: 'San Antonio', state: 'TX', lat: 29.4241, lng: -98.4936 },
  { name: 'San Diego', state: 'CA', lat: 32.7157, lng: -117.1611 },
  { name: 'Dallas', state: 'TX', lat: 32.7767, lng: -96.7970 },
  { name: 'San Jose', state: 'CA', lat: 37.3382, lng: -121.8863 },
];

const BUSINESS_NAMES: Record<string, string[]> = {
  'restaurant': ['Bella Vista', 'The Golden Fork', 'Sabor Latino', 'Dragon Palace', 'Smokehouse Grill', 'Taj Mahal', 'Bangkok Kitchen', 'Olive Grove'],
  'cafe': ['Bean There', 'Daily Grind', 'Morning Brew', 'Sweet Crumbs', 'Steep & Sip', 'Corner Cafe', 'Roast & Toast', 'The Coffee Lab'],
  'bar': ['The Tipsy Turtle', 'Midnight Oil', 'Hop Haven', 'Vino Velvet', 'Craft & Cork', 'Neon Nights', 'The Local', 'Last Call'],
  'retail': ['Style Station', 'Tech Hub', 'Home & Hearth', 'Page Turner', 'Active Life', 'Gemstone Jewelry', 'Urban Outfit', 'Vintage Vault'],
  'health': ['Bright Smiles Dental', 'Wellness Medical', 'Align Chiropractic', 'HealthFirst Pharmacy', 'Clear Vision Optometry', 'Mindful Therapy'],
  'beauty': ['Glamour Salon', 'Serenity Spa', 'Nailed It', 'Classic Cuts', 'Glow Skincare', 'Tranquil Touch Massage'],
  'fitness': ['Iron Temple', 'Zen Yoga Studio', 'Core Pilates', 'CrossFit Forge', 'Warrior Martial Arts', 'Blue Wave Pool'],
  'automotive': ['Precision Auto Repair', 'Metro Motors', 'Sparkle Car Wash', 'Tire Town', 'Auto Parts Plus'],
  'home': ['FlowRight Plumbing', 'Bright Spark Electric', 'Comfort Climate HVAC', 'Peak Roofing', 'Green Thumb Landscaping', 'Spotless Cleaning'],
  'professional': ['Sterling Law Group', 'Summit Accounting', 'Apex Consulting', 'Shield Insurance', 'Prime Properties'],
  'education': ['Bright Minds Academy', 'Excel Tutoring', 'Lingua Language', 'Harmony Music School', 'Canvas Art Studio', 'Rhythm Dance'],
  'entertainment': ['Starlight Theater', 'Cineplex Cinema', 'Strike Zone Bowling', 'Pixel Arcade', 'Enigma Escape', 'Heritage Museum'],
  'travel': ['Grand Plaza Hotel', 'Cozy Corner BnB', 'Wanderlust Travel', 'Adventure Tours', 'DriveEasy Rentals'],
  'finance': ['First National Bank', 'Community Credit Union', 'WealthWise Advisors', 'TaxPro Services', 'Growth Investments'],
  'legal': ['Justice Law Firm', 'Notary Now', 'Mediation Matters', 'Legal Aid Paralegals'],
  'real-estate': ['Keystone Realty', 'Horizon Property Mgmt', 'ValueView Appraisal', 'SureCheck Inspection'],
  'technology': ['CodeCraft Software', 'TechSupport Pro', 'WebWizards Design', 'SecureNet Cyber', 'DataDriven Analytics'],
  'construction': ['Cornerstone Contracting', 'Renew Remodeling', 'Solid Concrete', 'Current Electrical', 'PipeDream Plumbing'],
};

function getRandomElement<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function getRandomCoordinate(center: Coordinates, radiusKm: number): Coordinates {
  const radiusDeg = radiusKm / 111; // Rough conversion
  const u = Math.random();
  const v = Math.random();
  const w = radiusDeg * Math.sqrt(u);
  const t = 2 * Math.PI * v;
  const x = w * Math.cos(t);
  const y = w * Math.sin(t);
  return {
    lat: center.lat + y,
    lng: center.lng + x / Math.cos((center.lat * Math.PI) / 180),
  };
}

function generateMockBusiness(center: Coordinates, radiusKm: number = 5): Business {
  const category = getRandomElement(CATEGORIES);
  const subcategory = getRandomElement(SUBCATEGORIES[category] || ['general']);
  const names = BUSINESS_NAMES[category] || ['Business'];
  const name = `${getRandomElement(names)} ${getRandomElement(['Inc', 'LLC', 'Co', 'Group', 'Partners', ''])}`.trim();
  const city = getRandomElement(CITIES);
  const streetNum = Math.floor(Math.random() * 9999) + 1;
  const street = getRandomElement(STREET_NAMES);
  const coords = getRandomCoordinate(center, radiusKm);
  const hasWebsite = Math.random() > 0.4; // 60% have websites
  const websiteStatus = hasWebsite 
    ? (Math.random() > 0.7 ? 'modern' : 'outdated')
    : 'none';
  
  const leadType: Business['leadType'] = !hasWebsite ? 'no-website' 
    : websiteStatus === 'outdated' ? 'marketing' 
    : Math.random() > 0.5 ? 'b2b' : 'multiple';
  
  const baseScore = !hasWebsite ? 85 : websiteStatus === 'outdated' ? 70 : 40;
  const leadScore = Math.min(100, Math.max(0, baseScore + Math.floor(Math.random() * 30) - 15));
  
  return {
    id: generateId(),
    name,
    category,
    subcategory,
    address: `${streetNum} ${street}, ${city.name}, ${city.state}`,
    coordinates: coords,
    phone: `(${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
    email: hasWebsite ? `info@${name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com` : undefined,
    website: hasWebsite ? `https://${name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com` : undefined,
    websiteStatus,
    rating: hasWebsite ? Math.round((3.5 + Math.random() * 1.5) * 10) / 10 : undefined,
    reviewCount: hasWebsite ? Math.floor(Math.random() * 500) + 10 : undefined,
    photos: [],
    hours: {
      monday: { open: '09:00', close: '18:00' },
      tuesday: { open: '09:00', close: '18:00' },
      wednesday: { open: '09:00', close: '18:00' },
      thursday: { open: '09:00', close: '18:00' },
      friday: { open: '09:00', close: '18:00' },
      saturday: { open: '10:00', close: '16:00' },
      sunday: { open: 'closed', close: 'closed' },
    },
    socialMedia: hasWebsite ? {
      instagram: `@${name.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
      facebook: name.toLowerCase().replace(/[^a-z0-9]/g, ''),
    } : undefined,
    description: `Quality ${category} services in ${city.name}. Established business serving the local community.`,
    establishedYear: 2000 + Math.floor(Math.random() * 24),
    employeeCount: ['1-5', '6-15', '16-50', '51-200', '200+'][Math.floor(Math.random() * 5)],
    annualRevenue: ['<100K', '100K-500K', '500K-1M', '1M-5M', '5M+'][Math.floor(Math.random() * 5)],
    hasWebsite,
    websiteQuality: hasWebsite ? Math.floor(Math.random() * 100) : 0,
    needsMarketing: websiteStatus === 'outdated' || !hasWebsite,
    marketingGaps: !hasWebsite ? ['Website', 'SEO', 'Social Media', 'Online Reviews'] 
      : websiteStatus === 'outdated' ? ['Modern Website', 'SEO', 'Mobile Optimization'] 
      : ['SEO', 'Content Marketing', 'Paid Ads'],
    leadScore,
    leadType,
    source: getRandomElement(['mapbox', 'google-places', 'osm', 'manual', 'enriched']),
    lastUpdated: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
  };
}

// API functions
export const api = {
  // Fetch leads for a given area
  async fetchLeads(center: Coordinates, radiusKm: number = 5, count: number = 100): Promise<Business[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const leads: Business[] = [];
    for (let i = 0; i < count; i++) {
      leads.push(generateMockBusiness(center, radiusKm));
    }
    return leads;
  },
  
  // Search leads by query
  async searchLeads(query: string, center: Coordinates, radiusKm: number = 10): Promise<Business[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const allLeads = await this.fetchLeads(center, radiusKm, 200);
    const q = query.toLowerCase();
    return allLeads.filter(lead => 
      lead.name.toLowerCase().includes(q) ||
      lead.category.toLowerCase().includes(q) ||
      lead.address.toLowerCase().includes(q) ||
      lead.description?.toLowerCase().includes(q)
    );
  },
  
  // Get single lead details
  async getLead(id: string): Promise<Business | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    // In real app, fetch from backend
    return null;
  },
  
  // Enrich lead with more data
  async enrichLead(lead: Business): Promise<Business> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Simulate enrichment
    return {
      ...lead,
      email: lead.email || `contact@${lead.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
      phone: lead.phone || `(${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
      description: lead.description || `Professional ${lead.category} services. Established ${lead.establishedYear || 'recently'}.`,
      employeeCount: lead.employeeCount || ['1-5', '6-15', '16-50'][Math.floor(Math.random() * 3)],
      annualRevenue: lead.annualRevenue || ['<100K', '100K-500K', '500K-1M'][Math.floor(Math.random() * 3)],
      socialMedia: lead.socialMedia || {
        instagram: `@${lead.name.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
        facebook: lead.name.toLowerCase().replace(/[^a-z0-9]/g, ''),
      },
      source: 'enriched',
      lastUpdated: new Date().toISOString(),
    };
  },
  
  // Get website templates
  async getTemplates(): Promise<WebsiteTemplate[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      {
        id: 'modern-business',
        name: 'Modern Business',
        description: 'Clean, professional design perfect for service businesses',
        thumbnail: '/templates/modern-business.jpg',
        category: 'general',
        previewUrl: '/templates/modern-business/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'Get Started' }, order: 0 },
          { id: 'about', type: 'about', props: { title: 'About Us', content: '' }, order: 1 },
          { id: 'services', type: 'services', props: { title: 'Our Services', services: [] }, order: 2 },
          { id: 'testimonials', type: 'testimonials', props: { title: 'What Clients Say', testimonials: [] }, order: 3 },
          { id: 'contact', type: 'contact', props: { title: 'Contact Us' }, order: 4 },
        ],
      },
      {
        id: 'restaurant',
        name: 'Restaurant & Cafe',
        description: 'Warm, inviting design with menu showcase',
        thumbnail: '/templates/restaurant.jpg',
        category: 'restaurant',
        previewUrl: '/templates/restaurant/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'View Menu' }, order: 0 },
          { id: 'about', type: 'about', props: { title: 'Our Story', content: '' }, order: 1 },
          { id: 'menu', type: 'services', props: { title: 'Our Menu', services: [] }, order: 2 },
          { id: 'gallery', type: 'gallery', props: { title: 'Gallery', images: [] }, order: 3 },
          { id: 'hours', type: 'hours', props: { title: 'Hours & Location' }, order: 4 },
          { id: 'contact', type: 'contact', props: { title: 'Reservations' }, order: 5 },
        ],
      },
      {
        id: 'portfolio',
        name: 'Creative Portfolio',
        description: 'Visual-first design for creatives and agencies',
        thumbnail: '/templates/portfolio.jpg',
        category: 'technology',
        previewUrl: '/templates/portfolio/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'View Work' }, order: 0 },
          { id: 'work', type: 'gallery', props: { title: 'Selected Work', images: [] }, order: 1 },
          { id: 'services', type: 'services', props: { title: 'Services', services: [] }, order: 2 },
          { id: 'about', type: 'about', props: { title: 'About', content: '' }, order: 3 },
          { id: 'testimonials', type: 'testimonials', props: { title: 'Client Love', testimonials: [] }, order: 4 },
          { id: 'contact', type: 'contact', props: { title: 'Let\'s Talk' }, order: 5 },
        ],
      },
      {
        id: 'local-service',
        name: 'Local Service Pro',
        description: 'Trust-building design for home services and contractors',
        thumbnail: '/templates/local-service.jpg',
        category: 'home',
        previewUrl: '/templates/local-service/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'Get Free Quote' }, order: 0 },
          { id: 'trust', type: 'testimonials', props: { title: 'Trusted by Neighbors', testimonials: [] }, order: 1 },
          { id: 'services', type: 'services', props: { title: 'Our Services', services: [] }, order: 2 },
          { id: 'about', type: 'about', props: { title: 'Why Choose Us', content: '' }, order: 3 },
          { id: 'gallery', type: 'gallery', props: { title: 'Recent Projects', images: [] }, order: 4 },
          { id: 'contact', type: 'contact', props: { title: 'Schedule Service' }, order: 5 },
        ],
      },
      {
        id: 'health-wellness',
        name: 'Health & Wellness',
        description: 'Calming, trustworthy design for healthcare and wellness',
        thumbnail: '/templates/health-wellness.jpg',
        category: 'health',
        previewUrl: '/templates/health-wellness/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'Book Appointment' }, order: 0 },
          { id: 'services', type: 'services', props: { title: 'Our Services', services: [] }, order: 1 },
          { id: 'team', type: 'about', props: { title: 'Meet Our Team', content: '' }, order: 2 },
          { id: 'testimonials', type: 'testimonials', props: { title: 'Patient Stories', testimonials: [] }, order: 3 },
          { id: 'contact', type: 'contact', props: { title: 'Contact & Location' }, order: 4 },
        ],
      },
      {
        id: 'ecommerce',
        name: 'Local Shop',
        description: 'Product-focused design for retail and e-commerce',
        thumbnail: '/templates/ecommerce.jpg',
        category: 'retail',
        previewUrl: '/templates/ecommerce/preview',
        components: [
          { id: 'hero', type: 'hero', props: { headline: '', subheadline: '', ctaText: 'Shop Now' }, order: 0 },
          { id: 'featured', type: 'gallery', props: { title: 'Featured Products', images: [] }, order: 1 },
          { id: 'categories', type: 'services', props: { title: 'Shop by Category', services: [] }, order: 2 },
          { id: 'about', type: 'about', props: { title: 'Our Story', content: '' }, order: 3 },
          { id: 'reviews', type: 'testimonials', props: { title: 'Customer Reviews', testimonials: [] }, order: 4 },
          { id: 'contact', type: 'contact', props: { title: 'Visit Us' }, order: 5 },
        ],
      },
    ];
  },
  
  // Build website
  async buildWebsite(businessId: string, templateId: string, customizations: Record<string, any>): Promise<WebsiteBuild> {
    await new Promise(resolve => setTimeout(resolve, 3000));
    return {
      id: generateId(),
      businessId,
      templateId,
      userId: 'current-user',
      status: 'preview',
      customizations,
      previewUrl: `https://preview.leadmap3d.com/${generateId()}`,
      creditsUsed: 50,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },
  
  // Publish website
  async publishWebsite(buildId: string, domain?: string): Promise<{ url: string; domain?: string }> {
    await new Promise(resolve => setTimeout(resolve, 2000));
    return {
      url: `https://${domain || `business-${generateId()}.leadmap3d.com`}`,
      domain,
    };
  },
  
  // Create client
  async createClient(client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>): Promise<Client> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      ...client,
      id: generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },
  
  // Get pricing plans
  async getPricingPlans(): Promise<PricingPlan[]> {
    return [
      {
        id: 'free',
        name: 'Free',
        description: 'Explore the platform',
        monthlyPrice: 0,
        yearlyPrice: 0,
        credits: 100,
        features: ['Map-based lead finder', 'Basic lead filters', '5 website previews/month', 'Community support'],
        limits: { maxLeads: 50, maxWebsites: 5, maxClients: 3, apiAccess: false, whiteLabel: false, prioritySupport: false },
      },
      {
        id: 'starter',
        name: 'Starter',
        description: 'For solo entrepreneurs',
        monthlyPrice: 49,
        yearlyPrice: 490,
        credits: 2000,
        features: ['Everything in Free', '2,000 credits/month', '20 website builds/month', 'Client management', 'Email sequences', 'Priority support'],
        limits: { maxLeads: 500, maxWebsites: 20, maxClients: 20, apiAccess: false, whiteLabel: false, prioritySupport: true },
      },
      {
        id: 'pro',
        name: 'Pro',
        description: 'For growing agencies',
        monthlyPrice: 149,
        yearlyPrice: 1490,
        credits: 10000,
        features: ['Everything in Starter', '10,000 credits/month', '100 website builds/month', 'Team collaboration', 'White-label options', 'API access', 'Custom domains'],
        limits: { maxLeads: 5000, maxWebsites: 100, maxClients: 100, apiAccess: true, whiteLabel: true, prioritySupport: true },
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        description: 'For large organizations',
        monthlyPrice: 499,
        yearlyPrice: 4990,
        credits: 50000,
        features: ['Everything in Pro', '50,000 credits/month', 'Unlimited websites', 'Dedicated support', 'SLA guarantee', 'Custom integrations', 'SSO/SAML', 'Audit logs'],
        limits: { maxLeads: -1, maxWebsites: -1, maxClients: -1, apiAccess: true, whiteLabel: true, prioritySupport: true },
      },
    ];
  },
  
  // Geocoding
  async geocode(address: string): Promise<Coordinates[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    // Mock geocoding - in real app, use Mapbox Geocoding API or similar
    return CITIES.map(city => ({ lat: city.lat, lng: city.lng })).slice(0, 5);
  },
  
  // Reverse geocode
  async reverseGeocode(coords: Coordinates): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const city = CITIES.reduce((closest, city) => {
      const dist = Math.hypot(city.lat - coords.lat, city.lng - coords.lng);
      const closestDist = Math.hypot(closest.lat - coords.lat, closest.lng - coords.lng);
      return dist < closestDist ? city : closest;
    });
    return `${city.name}, ${city.state}`;
  },
};

export default api;